/**
 * @file FormatPropertiesLink.js
 * @description Properties panel for link container format configuration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import './PropertiesMultimedia.css';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import {
    formatFileSize,
    getFileCategory,
    getImageDimensions,
    sanitizeFileName,
} from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import ColorBar from '../theme/ColorBar';
import { getBootstrapIcons } from '../../utils/utils';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AddFromAssetsModal from '../ui/modals/AddFromAssetsModal';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { Popover, Box } from '@mui/material';
import Spinner from '../ui/spinners/Spinner';
import { ReactComponent as ImageIcon } from '../ui/svg/Add Image Placeholder.svg';
import { getText } from '../../i18n';

function FormatPropertiesLink({
    item,
    setItem,
    formatState,
    setFormatState,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
}) {
    const { publicBrand } = useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [file, setFile] = useState(null);
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [activeCard, setActiveCard] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [filter, setFilter] = useState('');
    const [imageOpen, setImageOpen] = useState(false);
    const [imageAnchorEl, setImageAnchorEl] = useState(null);
    const imagePopoverId = imageOpen ? 'image-popover' : undefined;
    const latestRequestIdRef = useRef(0);

    const {
        isDisplayIcon,
        selectedIcon,
        selectedBackgroundColor,
        selectedForegroundColor,
        image,
        imageAsset,
        isInitialized,
    } = formatState;

    const icons = getBootstrapIcons();
    const filteredIcons = icons.filter(icon =>
        icon.toString().includes(filter.toLowerCase()),
    );

    function updateFormatState(updates) {
        setFormatState(prev => ({ ...prev, ...updates }));
    }

    function handleImagePopoverClick(event) {
        setImageAnchorEl(event.currentTarget);
        setImageOpen(!imageOpen);
    }

    function handlePopoverClose() {
        setImageAnchorEl(null);
        setImageOpen(false);
    }

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked, handleUploadFromDevice]);

    useEffect(() => {
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked, handleAddFromAssets]);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        const containerAssets = [imageAsset].filter(
            a => a && Object.keys(a).length > 0,
        );

        setItem(prevState => {
            const { linkPart } = prevState;
            return {
                ...prevState,
                linkPart: {
                    ...linkPart,
                    glyph: isDisplayIcon ? selectedIcon : null,
                    backgroundColor: isDisplayIcon
                        ? selectedBackgroundColor
                        : null,
                    foregroundColor: isDisplayIcon
                        ? selectedForegroundColor
                        : null,
                    image: isDisplayIcon ? null : image,
                },
                containerAssets,
            };
        });
    }, [
        isDisplayIcon,
        selectedIcon,
        selectedBackgroundColor,
        selectedForegroundColor,
        image,
        imageAsset,
        isInitialized,
        setItem,
    ]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateFormatState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            const linkPart = item.linkPart || {};
            const hasGlyph =
                linkPart.glyph !== null && linkPart.glyph !== undefined;
            const hasImage =
                linkPart.image !== null &&
                linkPart.image !== undefined &&
                linkPart.image !== '';
            const shouldDisplayIcon = hasGlyph || !hasImage;

            let tempImageAsset = {};

            const hasContainerAssets =
                'containerAssets' in item &&
                item.containerAssets !== null &&
                item.containerAssets.length > 0;

            if (hasContainerAssets) {
                const filtered = item.containerAssets.filter(
                    assetItem => Object.keys(assetItem).length > 0,
                );

                for (const containerAsset of filtered) {
                    if (!containerAsset.assetId) {
                        continue;
                    }

                    const fetchedAsset = await fetchAssetByID(
                        containerAsset.assetId,
                        null,
                    );
                    const fileCategory = getFileCategory(fetchedAsset.url);

                    if (fileCategory === 'image') {
                        tempImageAsset = {
                            id: containerAsset.id,
                            assetId: containerAsset.assetId,
                            url: fetchedAsset.url,
                        };
                        break;
                    }
                }
            }

            updateFormatState({
                isDisplayIcon: shouldDisplayIcon,
                selectedIcon: linkPart.glyph || '',
                selectedBackgroundColor:
                    linkPart.backgroundColor || publicBrand.accessoryColor,
                selectedForegroundColor:
                    linkPart.foregroundColor || publicBrand.accessoryTextColor,
                image: tempImageAsset.url || linkPart.image || null,
                imageAsset: tempImageAsset,
                isInitialized: true,
            });

            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (isDisplayIcon) {
            updateFormatState({
                image: null,
                imageAsset: {},
            });
        }
    }, [isDisplayIcon, isInitialized]);

    function handleFileChange(fileValue) {
        setFile(sanitizeFileName(fileValue));
    }

    function handleIconSelected(icon) {
        const nextIcon = icon === selectedIcon ? '' : icon;
        updateFormatState({ selectedIcon: nextIcon });
    }

    function handleUpdateBackgroundColor(newSelection) {
        updateFormatState({ selectedBackgroundColor: newSelection.left });
    }

    function handleUpdateForegroundColor(newSelection) {
        updateFormatState({ selectedForegroundColor: newSelection.left });
    }

    function handleButtonLabelChange(event) {
        updateFormatState({ buttonText: event.target.value });
    }

    function clearImage() {
        updateFormatState({ image: null, imageAsset: {} });
    }

    function updateAssetField(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    function validateForm() {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    }

    async function uploadAsset() {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                    setIsSaving(false);
                    return;
                }
                urlResponse = encodeURI(response);
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({ ...prev, url: urlResponse }));
                } catch {
                    Toast.error(getText('msg_3e298875de25'));
                    setIsSaving(false);
                    return;
                }
            } catch {
                Toast.error(getText('msg_3e298875de25'));
                setIsSaving(false);
                return;
            }
        }

        if (!isValidUrl && !asset.url) {
            setIsSaving(false);
            return;
        }

        const newAsset = { ...asset };
        if (!newAsset.assetTypeId) {
            newAsset.assetTypeId = 4;
        }
        if (urlResponse) {
            newAsset.url = urlResponse;
        }
        newAsset.fileType = file.name.split('.').pop();
        newAsset.fileSize = formatFileSize(file.size);

        const fileCategory = getFileCategory(file.name);
        if (fileCategory === 'image') {
            newAsset.metaData = await getImageDimensions(file);
        }

        const createResponse = await createAsset(newAsset, null);

        if (createResponse.error) {
            Toast.error(createResponse.message);
            setIsSaving(false);
            return;
        }

        if (createResponse.status === 200 && createResponse.data.url) {
            Toast.success(getText('msg_9309271e92fe'));
            updateFormatState({
                image: createResponse.data.url,
                imageAsset: {
                    assetId: createResponse.data.id,
                    assetTypeId: createResponse.data.assetTypeId,
                    url: createResponse.data.url,
                },
            });
            setIsUploadFromDeviceChecked('');
            setAsset({});
            setFile(null);
            setIsSaving(false);
            return;
        }

        Toast.error(getText('msg_9f8fd8f97fd3'));
        setIsSaving(false);
    }

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsSearching(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page,
                    pageSize: 12,
                    fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
                };

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                if (!fetchedAssets) {
                    setAssets([]);
                    setTotalPages(1);
                    return;
                }

                setAssets(fetchedAssets.viewModels || []);
                setTotalPages(fetchedAssets.pageTotal || 1);
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsSearching(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm, setIsSaving],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    function handleSearch(event) {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    }

    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }
        debouncedLoadRef.current.cancel();
        setCurrentPage(1);
        loadAssets(1, searchTerm);
    }

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [isAddFromAssetsChecked, currentPage]);

    function handleClick(index) {
        setActiveCard(activeCard === index ? null : index);
    }

    function addAsset() {
        if (activeCard === null) {
            Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        const assetAdding = assets[activeCard];
        updateFormatState({
            image: assetAdding.url,
            imageAsset: {
                assetId: assetAdding.id,
                url: assetAdding.url,
            },
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function handleAssetFromLibrarySelected(assetAdding) {
        updateFormatState({
            image: assetAdding.url,
            imageAsset: {
                assetId: assetAdding.id,
                url: assetAdding.url,
            },
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function shouldHideForUploadOrAssets() {
        return isUploadFromDeviceChecked || isAddFromAssetsChecked;
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            {!shouldHideForUploadOrAssets() && (
                <>
                    <div
                        style={{
                            width: '100%',
                            minWidth: '100%',
                            display: 'flex',
                            gap: '20px',
                        }}
                    >
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="formatDisplayRadio"
                                id="formatDisplayIcon"
                                checked={isDisplayIcon === true}
                                onChange={() =>
                                    updateFormatState({ isDisplayIcon: true })
                                }
                                disabled={areInputsDisabled}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="formatDisplayIcon"
                            >
                                {getText('msg_794145f030ff')}
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="formatDisplayRadio"
                                id="formatDisplayImage"
                                checked={isDisplayIcon === false}
                                onChange={() =>
                                    updateFormatState({ isDisplayIcon: false })
                                }
                                disabled={areInputsDisabled}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="formatDisplayImage"
                            >
                                {getText('msg_50e19fda0d5b')}
                            </label>
                        </div>
                    </div>

                    {isDisplayIcon ? (
                        <>
                            <div style={{ marginTop: '20px' }}>
                                <div className="d-flex mb-3">
                                    <input
                                        type="text"
                                        style={{ width: '50%' }}
                                        className="form-control"
                                        placeholder={getText(
                                            'msg_2f149aaf731c',
                                        )}
                                        onChange={e =>
                                            setFilter(e.target.value)
                                        }
                                        disabled={areInputsDisabled}
                                    />
                                    {selectedIcon && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <i
                                                className={`bi bi-${selectedIcon}`}
                                                style={{
                                                    fontSize: '2em',
                                                    color: selectedForegroundColor,
                                                    margin: '0px 20px',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <label className="form-check-label">
                                                    {getText(
                                                        'msg_9a976fc228b6',
                                                    )}
                                                </label>
                                                <label className="form-check-label">
                                                    {selectedIcon}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    style={{
                                        border: '1px solid #2F2B4333',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        className={`d-flex flex-wrap ${areInputsDisabled ? 'disabled' : ''}`}
                                        style={{
                                            borderRadius: '12px',
                                            padding: '12px',
                                            height: '175px',
                                            overflow: areInputsDisabled
                                                ? 'hidden'
                                                : 'auto',
                                        }}
                                    >
                                        {filteredIcons.map((icon, index) => (
                                            <div
                                                key={index}
                                                className="px-2 d-flex"
                                                style={{
                                                    cursor: areInputsDisabled
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                    backgroundColor:
                                                        icon === selectedIcon
                                                            ? '#1366FF1A'
                                                            : 'transparent',
                                                    outline:
                                                        icon === selectedIcon
                                                            ? '1px solid #1366FF'
                                                            : 'none',
                                                    borderRadius: '8px',
                                                    aspectRatio: '1/1',
                                                    height: 'fit-content',
                                                    alignItems: 'center',
                                                }}
                                                onClick={() =>
                                                    !areInputsDisabled &&
                                                    handleIconSelected(icon)
                                                }
                                                onKeyDown={e =>
                                                    e.key === 'Enter' &&
                                                    !areInputsDisabled &&
                                                    handleIconSelected(icon)
                                                }
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <i
                                                    className={`bi bi-${icon}`}
                                                    style={{
                                                        fontSize: '24px',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <hr
                                style={{
                                    color: 'rgba(217, 217, 217, 1)',
                                }}
                            />

                            <div className="file-form-label mt-3">
                                {getText('msg_88d5e4cac104')}
                            </div>

                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div style={{ width: '50%' }}>
                                    <div className="file-form-label mt-3">
                                        {getText('msg_8b9ceea7c36f')}
                                    </div>
                                    <ColorBar
                                        label=""
                                        initialColors={{
                                            left: selectedBackgroundColor,
                                            right: selectedBackgroundColor,
                                            type: 'single',
                                        }}
                                        onChangeComplete={
                                            handleUpdateBackgroundColor
                                        }
                                        allowGradient={false}
                                        toolTipText=""
                                        startBelowHeader
                                    />
                                </div>

                                <div style={{ width: '50%' }}>
                                    <div className="file-form-label mt-3">
                                        {getText('msg_458918977019')}
                                    </div>
                                    <ColorBar
                                        label=""
                                        initialColors={{
                                            left: selectedForegroundColor,
                                            right: selectedForegroundColor,
                                            type: 'single',
                                        }}
                                        onChangeComplete={
                                            handleUpdateForegroundColor
                                        }
                                        allowGradient={false}
                                        toolTipText=""
                                        startBelowHeader
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div
                            style={{
                                marginTop: '20px',
                                display: areInputsDisabled ? 'none' : 'block',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        color: '#82808e',
                                    }}
                                >
                                    {getText('msg_659e19b4591f')}
                                </div>
                                <span
                                    onClick={handleImagePopoverClick}
                                    onKeyDown={e =>
                                        e.key === 'Enter' &&
                                        handleImagePopoverClick(e)
                                    }
                                    role="button"
                                    tabIndex={0}
                                    className="button-base ing-button-primary-left"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                                <Popover
                                    id={imagePopoverId}
                                    open={imageOpen}
                                    anchorEl={imageAnchorEl}
                                    onClose={handlePopoverClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 0,
                                            paddingBottom: 0,
                                            paddingTop: 0,
                                            lineHeight: '40px',
                                        }}
                                    >
                                        <div
                                            onClick={() => {
                                                setIsAddFromAssetsChecked(
                                                    'image',
                                                );
                                                setIsUploadFromDeviceChecked(
                                                    '',
                                                );
                                                setImageOpen(false);
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    setIsAddFromAssetsChecked(
                                                        'image',
                                                    );
                                                    setIsUploadFromDeviceChecked(
                                                        '',
                                                    );
                                                    setImageOpen(false);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            className="pointer-cursor profile-link"
                                            style={{ paddingRight: '80px' }}
                                        >
                                            {getText('msg_0bc609667aa7')}
                                        </div>
                                        <div
                                            onClick={() => {
                                                setIsAddFromAssetsChecked('');
                                                setIsUploadFromDeviceChecked(
                                                    'image',
                                                );
                                                setImageOpen(false);
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    setIsAddFromAssetsChecked(
                                                        '',
                                                    );
                                                    setIsUploadFromDeviceChecked(
                                                        'image',
                                                    );
                                                    setImageOpen(false);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            className="pointer-cursor profile-link"
                                            style={{ paddingRight: '80px' }}
                                        >
                                            {getText('msg_c002fe6df29c')}
                                        </div>
                                    </Box>
                                </Popover>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    height: '100%',
                                    width: '100%',
                                    margin: '20px 0',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        height: '300px',
                                        width: '500px',
                                        backgroundImage: image
                                            ? `url("${image}")`
                                            : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        outlineColor: image
                                            ? '#1366ff'
                                            : 'lightgrey',
                                        outlineStyle: image
                                            ? 'solid'
                                            : 'dashed',
                                        outlineWidth: '1px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {!image && <ImageIcon />}
                                    {image && (
                                        <i
                                            onClick={clearImage}
                                            onKeyDown={e =>
                                                e.key === 'Enter' &&
                                                clearImage()
                                            }
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                fontSize: '1.2em',
                                                color: 'red',
                                                cursor: 'pointer',
                                            }}
                                            className="bi bi-trash3"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAssetField(asset, 'name', value))
                        }
                        maxChars={256}
                        required
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(
                                    updateAssetField(asset, 'assetTags', tags),
                                )
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAssetField(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_5abd905257ee')}
                            checked={asset.assetTypeId !== 5}
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                            onKeyDown={e =>
                                e.key === 'Enter' &&
                                setIsUploadFromDeviceChecked('')
                            }
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={uploadAsset}
                            onKeyDown={e => e.key === 'Enter' && uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {isAddFromAssetsChecked && (
                <AddFromAssetsModal
                    title="Add from Library"
                    onYes={handleAssetFromLibrarySelected}
                    onNo={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    onHide={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    yesButtonText="Add"
                    noButtonText="Cancel"
                    acceptedFileTypes={['jpg', 'jpeg', 'png', 'svg']}
                    assetTypes={[1, 2]}
                    wasCreatedByMe={false}
                    displayModalBackdrop={false}
                />
            )}
        </>
    );
}

export default FormatPropertiesLink;
